import useSWR from 'swr';

import { fetchEndpointData } from '@utils/fetchEndpointData';

import type { QuotationRequestsResponse } from './endpoints/QuotationRequestsEndpoint.types';
import type { QuotationRequestResponse } from './endpoints/QuotationRequestEndpoint.types';

export type QuotationRequest = QuotationRequestResponse;

export const QUOTATION_REQUEST_SWR_KEY = 'quotation-request';

export interface QuotationRequestsFilters {
  search?: string;
}

/**
 * REST equivalent of the GraphQL `quotationRequests(id, take, filters)` query.
 * Cursor pagination (`cursor` maps to the `id` arg) + optional search filter.
 */
export async function getQuotationRequests(
  cursor?: string | null,
  take?: number,
  filters?: QuotationRequestsFilters,
  sortBy?: string,
  sortOrder?: string,
): Promise<QuotationRequestsResponse> {
  const params = new URLSearchParams();
  if (cursor) params.set('id', cursor);
  if (take != null) params.set('take', take.toString());
  if (filters?.search) params.set('search', filters.search);
  if (sortBy) params.set('sortBy', sortBy);
  if (sortOrder) params.set('sortOrder', sortOrder);

  const queryString = params.toString();
  const url = queryString ? `/api/v1/quotation-requests?${queryString}` : '/api/v1/quotation-requests';
  return fetchEndpointData<QuotationRequestsResponse>(url);
}

/**
 * REST equivalent of the GraphQL `quotationRequest(id)` query (single).
 */
export async function getQuotationRequest(id: string): Promise<QuotationRequestResponse> {
  return fetchEndpointData<QuotationRequestResponse>(`/api/v1/quotation-requests/${id}`);
}

/**
 * SWR hook for a single quotation request. Pass a falsy `id` to disable
 * fetching (mirrors urql's `pause` on the previous hook).
 */
export function useQuotationRequest(id: string | null | undefined) {
  return useSWR(id ? [QUOTATION_REQUEST_SWR_KEY, id] : null, () => getQuotationRequest(id!));
}
